// Short in-memory cache for admin reads that repeat on every navigation
// (rd-x2). The Worker allows 60 admin requests a minute from one address
// (RATE_LIMIT_ADMIN, keyed `admin:{ip}` before auth, reads and writes alike),
// and the shell asked for the same companies list, ledger accounts, P&L
// statement and queue again on almost every tab. One owner clicking quickly
// through the tabs reached 59 requests on the ten-click tour.
//
// Rules, each one load-bearing:
//   * At most 30 s. An entry's age counts from when its request STARTED, so a
//     slow read never stretches the window.
//   * One cache per AdminClient. A client is one sign-in and one role, so a
//     different role or token never shares answers. The key is the exact
//     request (path and query, or source method and arguments), and every
//     company-scoped read carries the company in it, so one company's answer
//     is never served for another.
//   * Any write through the client clears everything when its answer arrives
//     (failed or not). A read that started before then, even one still in
//     flight, is therefore never kept: nothing shows stale after the user acts.
//   * Identical reads already in flight share one request (coalescing).
//   * A failed read is never kept, so "Try again" asks the server.
//   * Each caller gets its own copy. A page that edits what it was given
//     cannot change what the next page sees.
//   * Only reads listed by the caller are cached (AdminClient's allow-list).
//     A read that is polled while waiting for something to change must not be
//     on that list, or the poll would see the same answer for 30 s.

/** The longest any answer is reused. The brief caps it at 30 seconds. */
export const READ_CACHE_TTL_MS = 30_000;

/** Bound memory if a session runs many distinct searches. */
export const READ_CACHE_MAX_ENTRIES = 200;

export interface ReadCacheOptions {
  /** Milliseconds; must be between 0 and READ_CACHE_TTL_MS. 0 turns caching off. */
  ttlMs?: number;
  /** Clock seam for tests. */
  now?: () => number;
}

interface Entry {
  startedAt: number;
  promise: Promise<unknown>;
}

export class ReadCache {
  private readonly ttlMs: number;
  private readonly now: () => number;
  private readonly entries = new Map<string, Entry>();

  constructor(options: ReadCacheOptions = {}) {
    const ttlMs = options.ttlMs ?? READ_CACHE_TTL_MS;
    if (!Number.isFinite(ttlMs) || ttlMs < 0 || ttlMs > READ_CACHE_TTL_MS) {
      throw new Error(
        `ReadCache: ttlMs must be 0-${String(READ_CACHE_TTL_MS)}, got ${String(ttlMs)}`,
      );
    }
    this.ttlMs = ttlMs;
    this.now = options.now ?? Date.now;
  }

  /** Number of answers held (in flight or settled). For tests. */
  get size(): number {
    return this.entries.size;
  }

  read<T>(key: string, load: () => Promise<T>): Promise<T> {
    const at = this.now();
    const hit = this.entries.get(key);
    if (hit !== undefined && this.fresh(hit, at)) {
      return hit.promise.then((value) => copyOf(value as T));
    }
    if (hit !== undefined) this.entries.delete(key);
    if (this.ttlMs === 0) return load();
    this.prune(at);
    let promise: Promise<T>;
    try {
      promise = load();
    } catch (error) {
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }
    const entry: Entry = { startedAt: at, promise };
    this.entries.set(key, entry);
    promise.catch(() => {
      if (this.entries.get(key) === entry) this.entries.delete(key);
    });
    return promise.then((value) => copyOf(value));
  }

  /** Forget every answer, including reads still in flight. */
  invalidate(): void {
    this.entries.clear();
  }

  private fresh(entry: Entry, at: number): boolean {
    // A clock that went backwards is treated as expired, never as fresh.
    return at >= entry.startedAt && at - entry.startedAt < this.ttlMs;
  }

  private prune(at: number): void {
    for (const [key, entry] of this.entries) {
      if (!this.fresh(entry, at)) this.entries.delete(key);
    }
    // Map iteration is insertion order, so the oldest go first.
    while (this.entries.size >= READ_CACHE_MAX_ENTRIES) {
      const oldest = this.entries.keys().next();
      if (oldest.done === true) break;
      this.entries.delete(oldest.value);
    }
  }
}

/**
 * A long-running action has settled: a Check now run, a statement or
 * prior-bookings upload being processed, or a new bank link appearing. Its
 * write was answered at once, but the change lands later, and a page may have
 * re-filled the cache while it ran. Call this when the poll that watches it
 * ends (done, failed or timed out), so the next read asks the server.
 *
 * Takes any object with an optional clearReadCache: some tests drive these
 * flows with a partial AdminClient that has no cache to clear.
 */
export function settleReads(client: { clearReadCache?: () => void }): void {
  client.clearReadCache?.();
}

function copyOf<T>(value: T): T {
  return value === undefined || value === null ? value : structuredClone(value);
}
